import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Page, PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { UserGear, UserPlus, Copy, Sparkle, Bell } from "@phosphor-icons/react";

const ROLE_LABEL = { admin: "Admin", manager: "Manager", agent: "Agent" };
const PLAT = { copilot360: "Copilot360", soysos: "SoySOS" };

export default function Users() {
  const qc = useQueryClient();
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/auth/users")).data });

  const update = useMutation({
    mutationFn: async ({ id, ...body }) => (await api.patch(`/auth/users/${id}`, body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); toast.success("Updated"); },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });
  const resend = useMutation({
    mutationFn: async (id) => (await api.post(`/auth/users/${id}/resend`)).data,
    onSuccess: (d) => { navigator.clipboard?.writeText(d.invite_link); toast.success("Invite re-sent — link copied"); },
  });

  const togglePlatform = (u, key) => {
    const has = (u.platforms || []).includes(key);
    const platforms = has ? u.platforms.filter((p) => p !== key) : [...(u.platforms || []), key];
    if (!platforms.length) return toast.error("Pick at least one platform");
    update.mutate({ id: u.id, platforms });
  };

  return (
    <Page>
      <PageHeader title="Users & Access" subtitle="Invite teammates, set their role and which platforms they can use.">
        <InviteDialog onDone={() => qc.invalidateQueries({ queryKey: ["users"] })} />
      </PageHeader>
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-secondary/50 text-muted-foreground text-[12px] uppercase tracking-wide">
            <tr>
              <th className="text-left font-semibold px-4 py-3">User</th>
              <th className="text-left font-semibold px-4 py-3">Role</th>
              <th className="text-left font-semibold px-4 py-3">Platforms</th>
              <th className="text-left font-semibold px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-border" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3"><div className="font-medium">{u.name}</div><div className="text-[12px] text-muted-foreground">{u.email}</div></td>
                <td className="px-4 py-3">
                  <Select value={u.role} onValueChange={(v) => update.mutate({ id: u.id, role: v })}>
                    <SelectTrigger className="h-8 w-28 text-[12px]" data-testid={`user-role-${u.email}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(ROLE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5">
                    {["copilot360", "soysos"].map((k) => {
                      const on = (u.platforms || []).includes(k);
                      const Icon = k === "soysos" ? Bell : Sparkle;
                      return (
                        <button key={k} data-testid={`user-plat-${k}-${u.email}`} onClick={() => togglePlatform(u, k)}
                          className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "bg-secondary text-muted-foreground border-border"}`}>
                          <Icon size={11} weight="fill" /> {PLAT[k]}
                        </button>
                      );
                    })}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full border ${u.status === "active" ? "bg-green-100 text-green-700 border-green-200" : u.status === "invited" ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-gray-100 text-gray-600 border-gray-200"}`}>{u.status}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  {u.status === "invited" && <Button size="sm" variant="ghost" className="text-[12px]" data-testid={`user-resend-${u.email}`} onClick={() => resend.mutate(u.id)}><Copy size={13} className="mr-1" /> Resend</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Page>
  );
}

function InviteDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ email: "", name: "", role: "agent", platforms: ["copilot360", "soysos"], department: "" });
  const [link, setLink] = useState("");
  const invite = useMutation({
    mutationFn: async () => (await api.post("/auth/users/invite", f)).data,
    onSuccess: (d) => { setLink(d.invite_link); toast.success("Invite sent"); onDone?.(); },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });
  const togglePlat = (k) => setF((s) => ({ ...s, platforms: s.platforms.includes(k) ? s.platforms.filter((p) => p !== k) : [...s.platforms, k] }));
  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setLink(""); }}>
      <DialogTrigger asChild><Button data-testid="invite-user-btn" className="gap-1.5"><UserPlus size={16} weight="fill" /> Invite user</Button></DialogTrigger>
      <DialogContent data-testid="invite-dialog">
        <DialogHeader><DialogTitle>Invite a user</DialogTitle><DialogDescription>They'll get an email to set their own password. Choose which platforms they can access.</DialogDescription></DialogHeader>
        {!link ? (
          <div className="space-y-3 py-1">
            <div><Label className="text-xs">Full name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="invite-name" className="mt-1" placeholder="Maria Lopez" /></div>
            <div><Label className="text-xs">Email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="invite-email" className="mt-1" placeholder="maria@hotel.com" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Role</Label>
                <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}><SelectTrigger className="mt-1" data-testid="invite-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(ROLE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label className="text-xs">Department</Label><Input value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} data-testid="invite-dept" className="mt-1" placeholder="Housekeeping" /></div>
            </div>
            <div>
              <Label className="text-xs">Platform access</Label>
              <div className="flex gap-2 mt-1.5">
                {["copilot360", "soysos"].map((k) => {
                  const on = f.platforms.includes(k);
                  return <button key={k} type="button" data-testid={`invite-plat-${k}`} onClick={() => togglePlat(k)} className={`text-[12px] px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "bg-secondary text-muted-foreground border-border"}`}>{PLAT[k]}</button>;
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="py-2">
            <p className="text-sm text-muted-foreground mb-2">Invite email sent. You can also share this link directly:</p>
            <div className="flex gap-2"><Input readOnly value={link} className="text-[12px]" data-testid="invite-link" /><Button size="sm" onClick={() => { navigator.clipboard?.writeText(link); toast.success("Copied"); }}><Copy size={14} /></Button></div>
          </div>
        )}
        {!link && <DialogFooter><Button onClick={() => invite.mutate()} disabled={!f.email || !f.name || invite.isPending || !f.platforms.length} data-testid="invite-submit">{invite.isPending ? "Sending…" : "Send invite"}</Button></DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}
